import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';

import styles from './index.module.scss';

import ConfirmSlider from '@/components/ui/ConfirmSlider';
import Spinner from '@/components/ui/Spinner';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';
import { sleep } from '@/utils/Sleep';
import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

// 상수 정의
const SURFACE_NUMBERS = [1, 2];
const SURFACE_PWM_MIN = 1100;
const SURFACE_PWM_MAX = 1800;

const Panel = ({ data: robots }) => {
  const [isProcessing, setProcessing] = useState(false);
  const [currentTest, setCurrentTest] = useState();
  const surfaceDefaultTrim = useRef({});
  const testTarget = useRef();

  const paramCommand = useParamCommand();

  useMountEffect(() => {
    robots.forEach((robot) => {
      surfaceDefaultTrim.current[robot.id] = {
        SERVO1_TRIM: Number(robot.metadata['SERVO1_DEFAULT_TRIM']),
        SERVO2_TRIM: Number(robot.metadata['SERVO2_DEFAULT_TRIM']),
      };
    });
  });

  //#region Process Functions
  const process = async (robot) => {
    try {
      // 전체 검사
      if (testTarget.current === 'all') {
        // 우측 타면
        setCurrentTest(1);
        await processSurface(robot, 'SERVO1_TRIM');

        // 좌측 타면
        setCurrentTest(2);
        await processSurface(robot, 'SERVO2_TRIM');
      }
      // 개별 검사
      else {
        let paramId;
        if (testTarget.current === 1) {
          paramId = 'SERVO1_TRIM';
        } else if (testTarget.current === 2) {
          paramId = 'SERVO2_TRIM';
        }

        setCurrentTest(testTarget.current);
        await processSurface(robot, paramId);
      }

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processSurface = async (robot, paramId) => {
    await executeMavLinkCommand(robot, paramCommand.set(paramId, SURFACE_PWM_MIN, 4));
    await sleep(1000);
    await executeMavLinkCommand(robot, paramCommand.set(paramId, SURFACE_PWM_MAX, 4));
    await sleep(1000);
    await executeMavLinkCommand(robot, paramCommand.set(paramId, surfaceDefaultTrim.current[robot.id][paramId], 4));
    await sleep(1000);
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.success(`Inspection completed successfully on ${robot.name}.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.error(`Inspection failed on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) return;

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const doStartAll = () => {
    testTarget.current = 'all';
    setProcessing(true);
  };

  const doStartSingle = (e) => {
    testTarget.current = Number(e.currentTarget.dataset.surface);
    setProcessing(true);
  };

  const doReset = (e) => {
    const key = e.currentTarget.dataset.key;

    robots.forEach((robot) => {
      executeMavLinkCommand(robot, paramCommand.set(key, surfaceDefaultTrim.current[robot.id][key], 4));
    });
  };

  const getActive = (number) => {
    return currentTest === number;
  };

  return (
    <div className={cx('container')}>
      <div className={cx('figure')}>
        <img src={require('./inspection-body.png')} alt="" className={cx('body')} />
        {/* Wing Right */}
        <div className={cx('wing', 'right')}>
          <img
            src={require('./inspection-surface-right.png')}
            alt=""
            className={cx(['surface', { active: getActive(SURFACE_NUMBERS[0]) }])}
          />
          <div className={cx('info')}>
            <svg width="36" height="36">
              <line
                x1="24"
                y1="0"
                x2="36"
                y2="64"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (R)</div>
              <button data-surface={1} onClick={doStartSingle} disabled={isProcessing}>
                {!getActive(SURFACE_NUMBERS[0]) && 'Start'}
                {getActive(SURFACE_NUMBERS[0]) && <Spinner size={14} />}
              </button>
              <button data-key="SERVO1_TRIM" onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
        {/* Wing Left */}
        <div className={cx('wing', 'left')}>
          <img
            src={require('./inspection-surface-left.png')}
            alt=""
            className={cx(['surface', { active: getActive(SURFACE_NUMBERS[1]) }])}
          />
          <div className={cx('info')}>
            <svg width="48" height="36">
              <line
                x1="48"
                y1="0"
                x2="36"
                y2="64"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Surface (L)</div>
              <button data-surface={2} onClick={doStartSingle} disabled={isProcessing}>
                {!getActive(SURFACE_NUMBERS[1]) && 'Start'}
                {getActive(SURFACE_NUMBERS[1]) && <Spinner size={14} />}
              </button>
              <button data-key="SERVO2_TRIM" onClick={doReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmSlider onConfirm={doStartAll} isProcessing={isProcessing} />
    </div>
  );
};

export default Panel;
